import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { hieraApi } from "../lib/api";
import { t, type Lang } from "../lib/i18n";
import { HeeArchGraph } from "../shell/HeeArchGraph";
import { HIERA_WEB_VERSION } from "../version";

type Props = { lang: Lang };

type Tab = "heearch" | "recent" | "favorites" | "mine";

type PageNode = {
  id: string;
  parent_id: string | null;
  title: string;
  icon: string | null;
  status: string;
  updated_at: string;
  created_at?: string;
  created_by?: string | null;
};

export function SpacePage({ lang }: Props) {
  const { spaceId = "" } = useParams();
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [pages, setPages] = useState<PageNode[]>([]);
  const [tab, setTab] = useState<Tab>("heearch");
  const [favIds, setFavIds] = useState<Set<string>>(new Set());
  const [meId, setMeId] = useState("");

  useEffect(() => {
    let dead = false;
    Promise.all([hieraApi.space(spaceId), hieraApi.pages(spaceId), hieraApi.home(), hieraApi.me()])
      .then(([sp, pg, home, me]) => {
        if (dead) return;
        setTitle(String(sp.space.title));
        setPages(
          pg.pages.map((row) => ({
            id: row.id,
            parent_id: row.parent_id,
            title: row.title,
            icon: row.icon,
            status: row.status,
            updated_at: row.updated_at,
            created_at: row.created_at,
            created_by: row.created_by,
          })),
        );
        setFavIds(
          new Set(
            home.favorites
              .filter((row) => String(row.space_id || "") === spaceId)
              .map((row) => String(row.page_id || ""))
              .filter(Boolean),
          ),
        );
        setMeId(me.id);
      })
      .catch(() => nav("/"));
    return () => {
      dead = true;
    };
  }, [spaceId, nav]);

  const list = useMemo(() => {
    if (tab === "favorites") {
      return pages
        .filter((p) => favIds.has(p.id))
        .slice()
        .sort((a, b) => +new Date(b.updated_at) - +new Date(a.updated_at));
    }
    if (tab === "mine") {
      return pages
        .filter((p) => meId && p.created_by === meId)
        .slice()
        .sort((a, b) => +new Date(b.created_at || b.updated_at) - +new Date(a.created_at || a.updated_at));
    }
    return pages.slice().sort((a, b) => +new Date(b.updated_at) - +new Date(a.updated_at));
  }, [pages, tab, favIds, meId]);

  const locale = lang === "ru" ? "ru-RU" : "en-GB";
  const tabs: Array<{ id: Tab; label: string; icon: "graph" | "clock" | "star" | "user" }> = [
    { id: "heearch", label: "HeeArch", icon: "graph" },
    { id: "recent", label: t(lang, "overviewRecent"), icon: "clock" },
    { id: "favorites", label: t(lang, "favorites"), icon: "star" },
    { id: "mine", label: t(lang, "overviewMine"), icon: "user" },
  ];

  return (
    <div className={`overview pad${tab === "heearch" ? " overview--graph" : ""}`}>
      <nav className="crumbs">
        <Link to="/">Hiera</Link>
        <span>/</span>
        <span>{title}</span>
      </nav>
      <h1 className="doc-heading">{title}</h1>
      <div className="overview-tabs" role="tablist">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={tab === item.id ? "is-on" : ""}
            onClick={() => setTab(item.id)}
          >
            <TabIcon kind={item.icon} />
            <span>{item.label}</span>
          </button>
        ))}
      </div>

      {tab === "heearch" ? (
        <HeeArchGraph
          pages={pages}
          emptyLabel={t(lang, "emptySpaceMascot")}
          pageKindLabel={t(lang, "archPage")}
          insideLabel={t(lang, "archInside")}
        />
      ) : list.length === 0 ? (
        <div className="mascot-empty">
          <img src={`/mascot.png?v=${HIERA_WEB_VERSION}`} alt="" width={72} height={72} />
          <p>
            {tab === "favorites"
              ? t(lang, "overviewFavEmpty")
              : tab === "mine"
                ? t(lang, "overviewMineEmpty")
                : t(lang, "emptySpaceMascot")}
          </p>
        </div>
      ) : (
        <ul className="doc-rows">
          {list.map((p) => (
            <li key={p.id}>
              <Link to={`/p/${p.id}`}>
                <span className="doc-row-ico" aria-hidden>
                  {p.icon || "📄"}
                </span>
                <strong>{p.title || "Untitled"}</strong>
                <time dateTime={p.updated_at}>
                  {new Date(tab === "mine" ? p.created_at || p.updated_at : p.updated_at).toLocaleString(locale)}
                </time>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TabIcon({ kind }: { kind: "graph" | "clock" | "star" | "user" }) {
  if (kind === "clock") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v4.5l3 1.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "star") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
        <path d="m12 3 2.4 5.4L20 9.2l-4 3.8.9 5.8L12 16.2 7.1 18.8 8 13 4 9.2l5.6-.8L12 3Z" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === "user") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
        <circle cx="12" cy="9" r="3.5" />
        <path d="M5 19c1.6-3 4-4.5 7-4.5s5.4 1.5 7 4.5" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <circle cx="6" cy="7" r="2.2" />
      <circle cx="18" cy="7" r="2.2" />
      <circle cx="12" cy="17" r="2.2" />
      <path d="M8 8.2 10.5 15M16 8.2 13.5 15" strokeLinecap="round" />
    </svg>
  );
}
